import React, { useState } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { useResponsive } from '../../hooks/useResponsive';

/**
 * MobileFilterDrawer - Mobile-optimized filter interface
 * Slides from bottom on mobile, shows as panel on desktop
 * Supports dark theme (used in analytics, campaigns pages)
 *
 * Props:
 * - isOpen: boolean - Whether drawer is open
 * - onClose: function - Called when closing
 * - filters: array - Filter definitions
 *   - [{ key, label, options: [{label, value}] }]
 * - selectedFilters: object - Current filter selections {key: value}
 * - onFilterChange: function(key, value) - Called when filter changes
 * - onApply: function - Called when apply button clicked
 * - onClear: function - Called when clear all button clicked
 * - title: string - Drawer title
 * - darkMode: boolean - Use dark theme (default true)
 */
const MobileFilterDrawer = ({
  isOpen = false,
  onClose = () => {},
  filters = [],
  selectedFilters = {},
  onFilterChange = () => {},
  onApply = () => {},
  onClear = () => {},
  title = 'Filtros',
  darkMode = true,
}) => {
  const { isMobile, isTablet } = useResponsive();
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initialState = {};
    filters.forEach(filter => {
      initialState[filter.key] = true; // Start all groups expanded
    });
    return initialState;
  });
  const [tempFilters, setTempFilters] = useState(selectedFilters);

  const handleFilterChange = (key, value) => {
    setTempFilters((prev) => ({
      ...prev,
      [key]: prev[key] === value ? null : value,
    }));
  };

  const handleApply = () => {
    Object.keys(tempFilters).forEach((key) => {
      onFilterChange(key, tempFilters[key]);
    });
    onApply?.();
    onClose();
  };

  const handleClear = () => {
    setTempFilters({});
    onClear?.();
  };

  const activeFilterCount = Object.values(tempFilters).filter(Boolean).length;

  // Color classes based on theme
  const bgClass = darkMode ? 'bg-slate-900' : 'bg-white';
  const borderClass = darkMode ? 'border-white/10' : 'border-gray-200';
  const textClass = darkMode ? 'text-white' : 'text-gray-900';
  const mutedTextClass = darkMode ? 'text-white/70' : 'text-gray-600';
  const hoverBgClass = darkMode ? 'hover:bg-white/10' : 'hover:bg-gray-50';
  const checkboxColorClass = darkMode ? 'accent-blue-500' : 'accent-blue-600';

  // Mobile & Tablet drawer - slides from bottom (isMobile OR isTablet)
  if (isMobile || isTablet) {
    return (
      <>
        {/* Backdrop */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm"
            onClick={onClose}
          />
        )}

        {/* Drawer - bottom sheet style */}
        <div
          className={`fixed bottom-0 left-0 right-0 ${bgClass} z-50 shadow-2xl rounded-t-2xl transition-transform duration-300 ease-out max-h-[85vh] flex flex-col ${
            isOpen ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b ${borderClass} sticky top-0 ${bgClass}`}>
            <div>
              <h2 className={`text-lg font-semibold ${textClass}`}>{title}</h2>
              {activeFilterCount > 0 && (
                <p className="text-sm text-blue-400 font-medium">
                  {activeFilterCount} activo{activeFilterCount !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className={`p-2 ${hoverBgClass} rounded-lg transition-colors active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-blue-500/50`}
              aria-label="Cerrar"
            >
              <X size={20} className={mutedTextClass} />
            </button>
          </div>

          {/* Filters - scrollable */}
          <div className="overflow-y-auto flex-1 px-4 pt-4 space-y-2 pb-4">
            {filters.map((filter) => {
              const isExpanded = expandedGroups[filter.key];
              const activeCount = Object.keys(tempFilters).filter(
                (key) => key === filter.key && tempFilters[key]
              ).length;
              return (
                <div key={filter.key} className={`border ${borderClass} rounded-lg overflow-hidden`}>
                  {/* Filter group header */}
                  <button
                    onClick={() =>
                      setExpandedGroups((prev) => ({
                        ...prev,
                        [filter.key]: !isExpanded,
                      }))
                    }
                    className={`w-full flex items-center justify-between p-3 ${hoverBgClass} transition-colors active:scale-[0.98]`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${textClass}`}>{filter.label}</span>
                      {activeCount > 0 && (
                        <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-0.5 rounded-full font-semibold">
                          {activeCount}
                        </span>
                      )}
                    </div>
                    <ChevronDown
                      size={18}
                      className={`${mutedTextClass} transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Filter options */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-out ${
                      isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className={`border-t ${borderClass} ${darkMode ? 'bg-gradient-to-b from-white/5 to-transparent' : 'bg-gradient-to-b from-gray-50 to-transparent'} space-y-1 p-2`}>
                      {filter.options?.map((option) => (
                        <label
                          key={option.value}
                          className={`flex items-center gap-3 p-2 cursor-pointer ${hoverBgClass} rounded transition-colors active:scale-[0.98]`}
                        >
                          <input
                            type="checkbox"
                            checked={tempFilters[filter.key] === option.value}
                            onChange={() =>
                              handleFilterChange(filter.key, option.value)
                            }
                            className={`w-4 h-4 rounded ${checkboxColorClass} cursor-pointer transition-colors duration-150`}
                          />
                          <span className={`text-sm ${textClass} flex-1 transition-colors duration-150`}>
                            {option.label}
                          </span>
                          {option.count !== undefined && (
                            <span className={`text-xs ${mutedTextClass} ${darkMode ? 'bg-white/10' : 'bg-gray-200'} px-2 py-1 rounded transition-colors duration-150`}>
                              {option.count}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer with actions */}
          <div className={`border-t ${borderClass} p-4 space-y-3 ${bgClass}`}>
            <button
              onClick={handleApply}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors min-h-[44px] active:scale-95 focus-visible:ring-2 focus-visible:ring-blue-500/50"
            >
              Aplicar {activeFilterCount > 0 && `(${activeFilterCount})`}
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={handleClear}
                className={`w-full border ${borderClass} ${textClass} py-3 rounded-lg font-medium ${hoverBgClass} transition-colors min-h-[44px] active:scale-95 focus-visible:ring-2 focus-visible:ring-blue-500/50`}
              >
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  // Desktop panel
  return (
    <div className={`${bgClass} rounded-lg border ${borderClass} p-6 space-y-4`}>
      <div>
        <h3 className={`text-lg font-semibold ${textClass}`}>{title}</h3>
        {activeFilterCount > 0 && (
          <p className="text-sm text-blue-400 font-medium mt-1">
            {activeFilterCount} filtro{activeFilterCount !== 1 ? 's' : ''} activo{activeFilterCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="space-y-4">
        {filters.map((filter) => (
          <div key={filter.key}>
            <h4 className={`font-medium ${textClass} mb-2`}>{filter.label}</h4>
            <div className="space-y-2">
              {filter.options?.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={tempFilters[filter.key] === option.value}
                    onChange={() => handleFilterChange(filter.key, option.value)}
                    className={`w-4 h-4 rounded ${checkboxColorClass} cursor-pointer`}
                  />
                  <span className={`text-sm ${textClass} flex-1`}>{option.label}</span>
                  {option.count !== undefined && (
                    <span className={`text-xs ${mutedTextClass}`}>{option.count}</span>
                  )}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className={`border-t ${borderClass} pt-4 space-y-2`}>
        <button
          onClick={handleApply}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium transition-colors"
        >
          Aplicar
        </button>
        {activeFilterCount > 0 && (
          <button
            onClick={handleClear}
            className={`w-full border ${borderClass} ${textClass} py-2 rounded-lg font-medium ${hoverBgClass} transition-colors`}
          >
            Limpiar filtros
          </button>
        )}
      </div>
    </div>
  );
};

export default MobileFilterDrawer;
